import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  UserIcon, 
  CalendarIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  SparklesIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { placementsAPI, tasksAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import TaskDetailModal from '../../components/Modals/TaskDetailModal';

// Types
interface Task {
  _id: string;
  title: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  dueDate: string;
  isCompleted: boolean;
  isOverdue: boolean;
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    trackerStatus: string;
    careLevel?: string;
    budget?: number;
    preferences?: {
      location?: string;
      amenities?: string[];
      specialties?: string[];
    };
  };
  assignedTo: {
    _id: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
  matchedFacilities?: Array<{
    facilityId: string;
    facilityName: string;
    compatibilityScore: number;
    reasons: string[];
    available: boolean;
  }>;
  progress: {
    percentage: number;
    milestones: Array<{
      name: string;
      completed: boolean;
      completedAt?: string;
    }>;
  };
  communications: Array<{
    id: string;
    type: 'note' | 'call' | 'email' | 'meeting';
    content: string;
    author: string;
    timestamp: string;
  }>;
  estimatedPlacementDate?: string;
  lastActivity: string;
  stage?: string;
}

interface Lane {
  tasks: Task[];
  count: number;
}

interface SwimLaneData {
  lanes: {
    [key: string]: Lane;
  };
}

// Task Card Component
const TaskCard: React.FC<{
  task: Task;
  isDragging?: boolean;
  onTaskClick?: (task: Task) => void;
}> = ({ task, isDragging = false, onTaskClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate progress percentage (fallback to 0 if not provided)
  const progressPercentage = task.progress?.percentage || 0;
  
  // Get highest compatibility score facility
  const bestMatch = task.matchedFacilities && task.matchedFacilities.length > 0
    ? task.matchedFacilities.reduce((best, current) => 
        current.compatibilityScore > best.compatibilityScore ? current : best
      )
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Only trigger click if not dragging
        if (!isDragging && !isSortableDragging && onTaskClick) {
          e.stopPropagation();
          onTaskClick(task);
        }
      }}
      className={`
        bg-white p-4 rounded-lg shadow-sm border border-gray-200 
        cursor-grab active:cursor-grabbing hover:cursor-pointer
        hover:shadow-md transition-all duration-200
        ${isDragging || isSortableDragging ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
      `}
    >
      {/* Header with Priority and Match Score */}
      <div className="flex items-center justify-between mb-3">
        <span className={`
          px-2 py-1 text-xs font-medium rounded-full border
          ${getPriorityColor(task.priority)}
        `}>
          {task.priority.toUpperCase()}
        </span>
        <div className="flex items-center gap-2">
          {bestMatch && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">
              {Math.round(bestMatch.compatibilityScore)}% match
            </span>
          )}
          {task.isOverdue && (
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>

      {/* Task Title */}
      <h4 className="font-medium text-gray-900 mb-2 text-sm leading-tight">
        {task.title}
      </h4>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600">Progress</span>
          <span className="text-xs text-gray-600">{progressPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Client Info */}
      <div className="flex items-center text-sm text-gray-600 mb-2">
        <UserIcon className="w-4 h-4 mr-1" />
        <span className="truncate">
          {task.client.firstName} {task.client.lastName}
        </span>
        {task.client.careLevel && (
          <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
            {task.client.careLevel}
          </span>
        )}
      </div>

      {/* Best Facility Match */}
      {bestMatch && (
        <div className="mb-2 p-2 bg-blue-50 rounded text-xs">
          <div className="font-medium text-blue-900">Best Match:</div>
          <div className="text-blue-700 truncate">{bestMatch.facilityName}</div>
          <div className="text-blue-600 text-xs mt-1">
            {bestMatch.reasons.slice(0, 2).join(', ')}
            {bestMatch.reasons.length > 2 && '...'}
          </div>
        </div>
      )}

      {/* Due Date & Estimated Placement */}
      <div className="space-y-1 mb-2">
        {task.dueDate && (
          <div className="flex items-center text-sm text-gray-500">
            <CalendarIcon className="w-4 h-4 mr-1" />
            <span>Due: {formatDate(task.dueDate)}</span>
          </div>
        )}
        {task.estimatedPlacementDate && (
          <div className="flex items-center text-sm text-green-600">
            <CalendarIcon className="w-4 h-4 mr-1" />
            <span>Est. Placement: {formatDate(task.estimatedPlacementDate)}</span>
          </div>
        )}
      </div>

      {/* Recent Communication */}
      {task.communications && task.communications.length > 0 && (
        <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
          <div className="font-medium text-gray-900">Last Update:</div>
          <div className="text-gray-700 truncate">
            {task.communications[task.communications.length - 1].content}
          </div>
          <div className="text-gray-500 text-xs mt-1">
            {formatDate(task.communications[task.communications.length - 1].timestamp)}
          </div>
        </div>
      )}

      {/* Assigned Agent */}
      <div className="text-xs text-gray-500 pt-2 border-t border-gray-100 flex justify-between items-center">
        <span>Assigned: {task.assignedTo.firstName} {task.assignedTo.lastName}</span>
        {task.assignedTo.role && (
          <span className="text-gray-400">({task.assignedTo.role})</span>
        )}
      </div>

      {/* Completion Status */}
      {task.isCompleted && (
        <div className="flex items-center text-xs text-green-600 mt-2">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          <span>Completed</span>
        </div>
      )}
    </div>
  );
};

// Swim Lane Column Component
const SwimLaneColumn: React.FC<{
  title: string;
  tasks: Task[];
  count: number;
  color: string;
  id: string;
  onTaskClick?: (task: Task) => void;
}> = ({ title, tasks, count, color, id, onTaskClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'orange': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'yellow': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'green': return 'bg-green-50 border-green-200 text-green-800';
      case 'purple': return 'bg-purple-50 border-purple-200 text-purple-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div 
      ref={setNodeRef}
      className={`
        bg-gray-50 rounded-lg p-4 min-h-[600px] flex flex-col transition-colors
        ${isOver ? 'bg-blue-50 ring-2 ring-blue-300' : ''}
      `}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        <span className={`
          px-2 py-1 text-xs font-medium rounded-full border
          ${getColorClasses(color)}
        `}>
          {count}
        </span>
      </div>

      {/* Tasks Container */}
      <SortableContext items={tasks.map(task => task._id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 flex-1">
          {tasks.map((task) => (
            <TaskCard key={task._id} task={task} onTaskClick={onTaskClick} />
          ))}
          {tasks.length === 0 && (
            <div className="text-center text-gray-500 text-sm py-8">
              <ClockIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No tasks in this stage</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

const SwimLaneTracker: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState<'standard' | 'analytics' | 'matching'>('standard');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMatchingSuggestions, setShowMatchingSuggestions] = useState(false);
  const [progressFilter, setProgressFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Handle task card click
  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  // Handle task update from modal
  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    // In a real app, this would make an API call
    console.log('Updating task:', taskId, updates);
    queryClient.invalidateQueries({ queryKey: ['swimLanes'] });
    showSuccess('Task updated successfully!');
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Fetch swim lane data
  const {
    data: swimLaneData,
    isLoading,
    error,
    refetch
  } = useQuery<{ data: SwimLaneData }>({
    queryKey: ['swimLanes'],
    queryFn: () => placementsAPI.getSwimLanes(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Update task stage mutation
  const updateTaskStageMutation = useMutation({
    mutationFn: ({ taskId, newStage }: { taskId: string; newStage: string }) => {
      console.log('Attempting to move task:', taskId, 'to stage:', newStage);
      console.log('Valid stages are:', ['New Client', 'Active Client', 'Target', 'Placed', 'Follow-up']);
      return tasksAPI.updateTaskStatus(taskId, newStage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swimLanes'] });
      showSuccess('Task moved successfully!');
    },
    onError: (error: any) => {
      console.error('Error moving task:', error);
      console.error('Error response:', error.response?.data);
      showError(`Failed to move task: ${error.response?.data?.message || error.message || 'Please try again.'}`);
    },
  });

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const taskId = active.id as string;
    
    // Find the task being dragged
    if ((swimLaneData as any)?.data?.data?.lanes) {
      for (const lane of Object.values((swimLaneData as any).data.data.lanes)) {
        const task = (lane as any).tasks.find((t: any) => t._id === taskId);
        if (task) {
          setDraggedTask(task);
          break;
        }
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedTask(null);

    if (!over) return;

    const taskId = active.id as string;
    let targetLane = over.id as string;

    // Check if targetLane is a valid status, if not it might be a task ID
    const validStatuses = ['New Client', 'Active Client', 'Target', 'Placed', 'Follow-up'];
    
    if (!validStatuses.includes(targetLane)) {
      console.log('Invalid target lane detected:', targetLane, 'might be a task ID');
      
      // If we're dropping on a task, find which lane that task belongs to
      if ((swimLaneData as any)?.data?.data?.lanes) {
        for (const [laneKey, laneData] of Object.entries((swimLaneData as any).data.data.lanes)) {
          if ((laneData as any).tasks.some((task: any) => task._id === targetLane)) {
            targetLane = laneKey;
            console.log('Found target lane from task:', targetLane);
            break;
          }
        }
      }
    }

    console.log('Final target lane:', targetLane);

    // If dropped on the same lane, do nothing
    if (draggedTask && (swimLaneData as any)?.data?.data?.lanes) {
      const currentLane = Object.keys((swimLaneData as any).data.data.lanes).find(laneKey =>
        (swimLaneData as any).data.data.lanes[laneKey].tasks.some((task: any) => task._id === taskId)
      );

      if (currentLane !== targetLane) {
        updateTaskStageMutation.mutate({
          taskId,
          newStage: targetLane
        });
      }
    }
  };

  // Filter tasks based on search, priority, progress, and assignee
  const getFilteredTasks = (tasks: Task[]) => {
    console.log('Filtering tasks:', tasks.length, 'tasks with filters:', {
      searchTerm,
      priorityFilter,
      progressFilter,
      assigneeFilter
    });

    // Debug: Show first task structure
    if (tasks.length > 0) {
      console.log('Sample task structure:', tasks[0]);
    }

    const filtered = tasks.filter(task => {
      const matchesSearch = searchTerm === '' ||
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${task.client.firstName} ${task.client.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.assignedTo && `${task.assignedTo.firstName} ${task.assignedTo.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesPriority = priorityFilter === 'all' || 
        task.priority.toLowerCase() === priorityFilter.toLowerCase();
      
      // Debug priority matching
      if (priorityFilter !== 'all') {
        console.log('Priority filter debug:', {
          taskTitle: task.title,
          taskPriority: task.priority,
          taskPriorityType: typeof task.priority,
          filterValue: priorityFilter,
          filterValueType: typeof priorityFilter,
          matches: task.priority.toLowerCase() === priorityFilter.toLowerCase()
        });
      }
      
      const matchesProgress = progressFilter === 'all' || 
        (progressFilter === 'low' && (task.progress?.percentage || 0) < 25) ||
        (progressFilter === 'medium' && (task.progress?.percentage || 0) >= 25 && (task.progress?.percentage || 0) < 75) ||
        (progressFilter === 'high' && (task.progress?.percentage || 0) >= 75);
      
      const matchesAssignee = assigneeFilter === 'all' || 
        (task.assignedTo && `${task.assignedTo.firstName} ${task.assignedTo.lastName}` === assigneeFilter);
      
      const matches = matchesSearch && matchesPriority && matchesProgress && matchesAssignee;
      
      if (!matches) {
        console.log('Task filtered out:', task.title, {
          matchesSearch,
          matchesPriority,
          matchesProgress,
          matchesAssignee,
          taskData: {
            title: task.title,
            priority: task.priority,
            progress: task.progress?.percentage,
            assignedTo: task.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}` : null
          }
        });
      }

      return matches;
    });

    console.log('Filtered results:', filtered.length, 'tasks');
    return filtered;
  };

  // Smart matching algorithm
  const calculateCompatibilityScore = (client: Task['client'], facility: any) => {
    let score = 0;
    let reasons = [];

    // Budget matching (40% weight)
    if (client.budget && facility.pricing) {
      const budgetMatch = client.budget >= facility.pricing.baseRate * 0.9 && 
                         client.budget <= facility.pricing.baseRate * 1.1;
      if (budgetMatch) {
        score += 40;
        reasons.push('Budget compatible');
      }
    }

    // Care level matching (30% weight)
    if (client.careLevel && facility.careTypes?.includes(client.careLevel)) {
      score += 30;
      reasons.push('Care level match');
    }

    // Location preferences (20% weight)
    if (client.preferences?.location && facility.address?.city === client.preferences.location) {
      score += 20;
      reasons.push('Location preference');
    }

    // Amenities matching (10% weight)
    if (client.preferences?.amenities && facility.amenities) {
      const matchingAmenities = client.preferences.amenities.filter(amenity => 
        facility.amenities.includes(amenity)
      );
      if (matchingAmenities.length > 0) {
        score += 10;
        reasons.push(`${matchingAmenities.length} amenity matches`);
      }
    }

    return { score, reasons };
  };

  // Get unique assignees for filter
  const getUniqueAssignees = () => {
    const assignees = new Set<string>();
    if ((swimLaneData as any)?.data?.data?.lanes) {
      Object.values((swimLaneData as any).data.data.lanes).forEach((lane: any) => {
        lane.tasks.forEach((task: Task) => {
          if (task.assignedTo) {
            assignees.add(`${task.assignedTo.firstName} ${task.assignedTo.lastName}`);
          }
        });
      });
    }
    const result = Array.from(assignees);
    console.log('Available assignees:', result);
    return result;
  };

  // Lane configuration
  const laneConfig = [
    { key: 'New Client', title: 'New Client', color: 'blue' },
    { key: 'Active Client', title: 'Active Client', color: 'orange' },
    { key: 'Target', title: 'Target', color: 'yellow' },
    { key: 'Placed', title: 'Placed', color: 'green' },
    { key: 'Follow-up', title: 'Follow-up', color: 'purple' },
  ];

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load swim lane data. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Enhanced Header with View Mode Toggle */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-left">🏊‍♂️ Advanced Swim Lane Tracker</h1>
          <p className="text-gray-600">
            Placement management with smart matching and real-time analytics
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <button
            onClick={() => setViewMode('standard')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'standard' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Standard View
          </button>
          <button
            onClick={() => setViewMode('analytics')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'analytics' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setShowMatchingSuggestions(!showMatchingSuggestions)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showMatchingSuggestions 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Smart Matching
          </button>
        </div>
      </div>

      {/* Enhanced Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Tasks, clients, agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Priorities</option>
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          {/* Progress Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Progress</label>
            <select
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="all">All Progress</option>
              <option value="low">0-25%</option>
              <option value="medium">25-75%</option>
              <option value="high">75-100%</option>
            </select>
          </div>

          {/* Assignee Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Assignee</label>
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="all">All Agents</option>
              {getUniqueAssignees().map((assignee) => (
                <option key={assignee} value={assignee}>{assignee}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Swim Lanes */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {laneConfig.map((config) => {
            const laneData = (swimLaneData as any)?.data?.data?.lanes?.[config.key] || { tasks: [], count: 0 };
            const filteredTasks = getFilteredTasks(laneData.tasks);

            return (
              <div
                key={config.key}
                className="drop-zone"
              >
                <SwimLaneColumn
                  id={config.key}
                  title={config.title}
                  tasks={filteredTasks}
                  count={filteredTasks.length}
                  color={config.color}
                  onTaskClick={handleTaskClick}
                />
              </div>
            );
          })}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {draggedTask ? (
            <TaskCard task={draggedTask} isDragging />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Enhanced Analytics Dashboard */}
      <div className="mt-6 space-y-6">
        {/* Lane Stats */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {laneConfig.map((config) => {
              const laneData = (swimLaneData as any)?.data?.data?.lanes?.[config.key] || { tasks: [], count: 0 };
              const filteredTasks = getFilteredTasks(laneData.tasks);
              const avgProgress = filteredTasks.length > 0 
                ? filteredTasks.reduce((sum, task) => sum + (task.progress?.percentage || 0), 0) / filteredTasks.length 
                : 0;
              
              return (
                <div key={config.key} className="text-center">
                  <div className="text-3xl font-bold text-gray-900">{filteredTasks.length}</div>
                  <div className="text-sm text-gray-600 mb-2">{config.title}</div>
                  <div className="text-xs text-gray-500">
                    Avg: {Math.round(avgProgress)}% complete
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Analytics View */}
        {viewMode === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Metrics */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h4>
              <div className="space-y-4">
                {(() => {
                  const allTasks: Task[] = [];
                  if ((swimLaneData as any)?.data?.data?.lanes) {
                    Object.values((swimLaneData as any).data.data.lanes).forEach((lane: any) => {
                      allTasks.push(...lane.tasks);
                    });
                  }
                  
                  const overdueTasks = allTasks.filter(task => task.isOverdue).length;
                  const highPriorityTasks = allTasks.filter(task => task.priority === 'Urgent' || task.priority === 'High').length;
                  const completedTasks = allTasks.filter(task => task.isCompleted).length;
                  const avgProgress = allTasks.length > 0 
                    ? allTasks.reduce((sum, task) => sum + (task.progress?.percentage || 0), 0) / allTasks.length 
                    : 0;

                  return (
                    <>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                        <span className="text-red-800 font-medium">Overdue Tasks</span>
                        <span className="text-red-900 font-bold">{overdueTasks}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-orange-50 rounded">
                        <span className="text-orange-800 font-medium">High Priority</span>
                        <span className="text-orange-900 font-bold">{highPriorityTasks}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                        <span className="text-green-800 font-medium">Completed</span>
                        <span className="text-green-900 font-bold">{completedTasks}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                        <span className="text-blue-800 font-medium">Avg Progress</span>
                        <span className="text-blue-900 font-bold">{Math.round(avgProgress)}%</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Team Performance */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Team Performance</h4>
              <div className="space-y-3">
                {(() => {
                  const teamStats: { [key: string]: { tasks: number; completed: number; avgProgress: number } } = {};
                  
                  if ((swimLaneData as any)?.data?.data?.lanes) {
                    Object.values((swimLaneData as any).data.data.lanes).forEach((lane: any) => {
                      lane.tasks.forEach((task: Task) => {
                        if (task.assignedTo) {
                          const name = `${task.assignedTo.firstName} ${task.assignedTo.lastName}`;
                          if (!teamStats[name]) {
                            teamStats[name] = { tasks: 0, completed: 0, avgProgress: 0 };
                          }
                          teamStats[name].tasks++;
                          if (task.isCompleted) teamStats[name].completed++;
                          teamStats[name].avgProgress += (task.progress?.percentage || 0);
                        }
                      });
                    });
                  }

                  return Object.entries(teamStats).map(([name, stats]) => {
                    const avgProgress = stats.tasks > 0 ? stats.avgProgress / stats.tasks : 0;
                    const completionRate = stats.tasks > 0 ? (stats.completed / stats.tasks) * 100 : 0;
                    
                    return (
                      <div key={name} className="p-3 border border-gray-200 rounded">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{name}</span>
                          <span className="text-sm text-gray-600">{stats.tasks} tasks</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Completion: {Math.round(completionRate)}%</span>
                          <span>Avg Progress: {Math.round(avgProgress)}%</span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Smart Matching Suggestions */}
        {showMatchingSuggestions && (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">🤖 Smart Placement Suggestions</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {(() => {
                const suggestionsData: any[] = [];
                
                if ((swimLaneData as any)?.data?.data?.lanes) {
                  Object.values((swimLaneData as any).data.data.lanes).forEach((lane: any) => {
                    lane.tasks.forEach((task: Task) => {
                      if (task.matchedFacilities && task.matchedFacilities.length > 0) {
                        const bestMatch = task.matchedFacilities.reduce((best, current) => 
                          current.compatibilityScore > best.compatibilityScore ? current : best
                        );
                        suggestionsData.push({ task, bestMatch });
                      }
                    });
                  });
                }

                return suggestionsData.slice(0, 6).map(({ task, bestMatch }, index) => (
                  <div key={task._id} className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-blue-900">
                          {task.client.firstName} {task.client.lastName}
                        </div>
                        <div className="text-sm text-blue-700">{task.title}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-900">
                          {Math.round(bestMatch.compatibilityScore)}%
                        </div>
                        <div className="text-xs text-blue-700">match</div>
                      </div>
                    </div>
                    <div className="text-sm text-blue-800 mb-2 font-medium">
                      {bestMatch.facilityName}
                    </div>
                    <div className="text-xs text-blue-600">
                      {bestMatch.reasons.join(' • ')}
                    </div>
                    {bestMatch.available && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Available Now
                        </span>
                      </div>
                    )}
                  </div>
                ));
              })()}
              {(() => {
                const suggestionsCount = (() => {
                  let count = 0;
                  if ((swimLaneData as any)?.data?.data?.lanes) {
                    Object.values((swimLaneData as any).data.data.lanes).forEach((lane: any) => {
                      lane.tasks.forEach((task: Task) => {
                        if (task.matchedFacilities && task.matchedFacilities.length > 0) count++;
                      });
                    });
                  }
                  return count;
                })();
                
                return suggestionsCount === 0 ? (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">🔍</div>
                    <p>No smart matching data available</p>
                    <p className="text-sm">Add client preferences and facility data to see smart suggestions</p>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onUpdateTask={handleUpdateTask}
      />
    </div>
  );
};

export default SwimLaneTracker;
